// frontend/src/utils/excelWorksheet.js
// Turns clipboard HTML pasted from Excel into a clean, self-contained fragment.
// Preserves fill colour, font colour and REAL cell borders (from Excel's own
// <style> classes / inline styles). Removes fixed column widths (so blank
// middle columns collapse instead of stretching) and adds NO blanket cell
// border (so Excel gridlines never appear on empty cells).

export function cleanExcelHtml(rawHtml) {
    if (!rawHtml || !rawHtml.trim()) return '';

    const doc = new DOMParser().parseFromString(rawHtml, 'text/html');

    // Keep Excel's <style> blocks — they carry .xlNN classes with
    // background / color / border. Drop only the mso-* noise.
    let styleText = '';
    doc.querySelectorAll('style').forEach((s) => { styleText += s.textContent + '\n'; });
    styleText = styleText.replace(/mso-[^;:{}]+:[^;}]+;?/gi, '');

    const tables = Array.from(doc.querySelectorAll('table'));
    if (tables.length === 0) {
        // No table (plain text range) — return the body text as-is, escaped.
        const txt = (doc.body?.textContent || '').trim();
        return txt ? `<div>${escapeHtml(txt)}</div>` : '';
    }

    const stripDims = (el) => {
        el.removeAttribute('width');
        el.removeAttribute('height');
        if (el.style) {
            el.style.removeProperty('width');
            //el.style.removeProperty('height');
        }
    };

    const cleanTable = (table) => {
        // <col>/<colgroup> only carry fixed widths -> remove so columns auto-size
        table.querySelectorAll('colgroup, col').forEach((c) => c.remove());
        table.querySelectorAll('script, meta, link').forEach((n) => n.remove());
        stripDims(table);
        table.removeAttribute('border');
        table.setAttribute('cellspacing', '0');
        table.setAttribute('cellpadding', '0');

        table.querySelectorAll('tr, td, th').forEach((cell) => {
            stripDims(cell);
            // strip event handlers (defensive)
            [...cell.attributes].forEach((a) => {
                if (/^on/i.test(a.name)) cell.removeAttribute(a.name);
            });
        });
        return table.outerHTML;
    };

    const tablesHtml = tables.map(cleanTable).join('\n');
    return `<style>${styleText}</style>\n${tablesHtml}`;
}

// Wraps the cleaned fragment in a minimal document for the sandboxed iframe.
// NOTE: no blanket td/th border here -> no gridlines. No fixed table width ->
// wide ledgers scroll horizontally (the iframe is its own scroll viewport)
// instead of being squashed.
export function buildWorksheetDoc(fragment) {
    return `<!DOCTYPE html><html><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<style>
  html, body { margin: 0; padding: 0; }
  body { padding: 6px; font-family: Arial, Helvetica, sans-serif; font-size: 14px; color: #1a1d29; background: transparent; }
  table { border-collapse: collapse; table-layout: auto; }
  td, th { padding: 2px 6px; vertical-align: top; }
  img { max-width: 100%; height: auto; }
</style></head>
<body>${fragment || ''}</body></html>`;
}

function escapeHtml(s) {
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}